import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, Injector, resource, computed, signal } from "@angular/core";
import { firstValueFrom, filter, take, Observable, Subject } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import type { IDashboardModel, IDashboardApiResponse, IDashboardUpdateRequest, IDashboardUpdateResponse, IApplication } from "@app/views-interfaces";
import { AppSettingsService } from "@shared/app-settings-service";
import { AuthService, type CurrentUser } from "@shared/auth";
import { LocalStorageService } from "@shared/local-storage";
import { isiFirmTax, isJobsBilling, isJobsBillingDashboard, isiFirmTaxDashboard, isTaxprep } from "@app/views-interfaces";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { FirmsService } from "@shared/firms-service";
import { UserDashboardSettingsService } from "@shared/user-dashboard-settings-service";
import type { IUserDashboardSettingsForMe } from "@interfaces";

interface AccessibleDashboardsResource {
    dashboards: IDashboardModel[] | undefined;
    applications: IApplication[] | undefined;
    user: CurrentUser | null | undefined;
    userDashboardSettings: IUserDashboardSettingsForMe | undefined;
}

@Injectable({ providedIn: "root" })
export class DashboardsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly firmsService = inject(FirmsService);
    private readonly http = inject(HttpClient);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly injector = inject(Injector);
    private userDashboardSettingsService?: UserDashboardSettingsService;

    private getUserDashboardSettingsService(): UserDashboardSettingsService {
        return (this.userDashboardSettingsService ??= this.injector.get(UserDashboardSettingsService));
    }

    public titleChangedEmitter = new Subject<string>();
    public titleChangedEvent$ = this.titleChangedEmitter.asObservable();

    private dashboardIdKeyCached: string | undefined;
    private isReloading = true;
    private _currentId = signal<string>("");
    public readonly currentId = this._currentId.asReadonly();
    private dashboardsCached: IDashboardModel[] | undefined;
    private currentDashboardId: string | undefined | null;

    private currentDashboarChangedEmitter = new Subject<string>();
    public currentDashboardChanged$ = this.currentDashboarChangedEmitter.asObservable();
    public dashboardChangeEmitter = new Subject<void>();
    public dashboardChangeEvent$ = this.dashboardChangeEmitter.asObservable();

    private dashboardIdKeyResource = resource<string | undefined, string | undefined>({
        params: () => this.firmsService.id(),
        loader: async ({ params: firmId }) => {
            if (!firmId) return;

            this.dashboardIdKeyCached = "currentDashboard_" + firmId;
            return this.dashboardIdKeyCached;
        },
    });
    public readonly dashboardIdKey = computed(() => this.dashboardIdKeyResource.value());
    private readonly dashboardIdKey$ = toObservable(this.dashboardIdKey);

    private dashboardsResource = resource<IDashboardModel[] | undefined, CurrentUser | null>({
        params: () => this.authService.currentUser(),
        loader: async ({ params: currentUser }) => {
            if (!currentUser) return;
            if (!this.isReloading && this.dashboardsCached) return this.dashboardsCached;
            this.isReloading = false;
            this.dashboardsCached = await this.loadDashboards();
            return this.dashboardsCached;
        },
    });
    public readonly dashboards = computed(() => this.dashboardsResource.value());
    private readonly dashboards$ = toObservable(this.dashboards);

    private readonly accessibleDashboardsResource = resource<IDashboardModel[] | undefined, AccessibleDashboardsResource>({
        params: () => ({
            dashboards: this.dashboards(),
            applications: this.firmsService.applications(),
            user: this.authService.currentUser(),
            userDashboardSettings: this.getUserDashboardSettingsService().userDashboardSettingsForMe(),
        }),
        loader: async ({ params }) => {
            const { dashboards, applications, userDashboardSettings, user } = params;
            const userDashboardList = user?.have_rights_dashboard_list ?? user?.dashboard_list ?? [];
            if (!dashboards || !dashboards.length || !applications || !applications.length || !userDashboardList.length) return;

            const firmApplicationDashboardIds = new Set(applications.map((application) => application.Value) ?? []);
            const userDashboardIds = userDashboardList.reduce((acc, item) => {
                acc.push(item.split("_")[0]);
                return acc;
            }, [] as string[]);

            return dashboards
                .filter((dashboard) => {
                    if (!firmApplicationDashboardIds.has(dashboard.Id) || !userDashboardIds.includes(dashboard.Id)) {
                        return false;
                    }

                    const setting = userDashboardSettings?.get(dashboard.Id);
                    return !setting || setting.Active;
                })
                .sort((a, b) => {
                    if (a.CreationDate < b.CreationDate) {
                        return -1;
                    } else if (a.CreationDate > b.CreationDate) {
                        return 1;
                    } else {
                        return 0;
                    }
                });
        },
    });
    public readonly accessibleDashboards = computed(() => this.accessibleDashboardsResource.value());
    private readonly accessibleDashboards$ = toObservable(this.accessibleDashboards);

    public readonly dashboardsById = computed(() => {
        const items = this.dashboards();
        if (!items || !items.length) return;

        const dashboards = new Map<string, IDashboardModel>();
        for (const item of items) {
            dashboards.set(item.Id, item);
        }
        return dashboards;
    });

    public readonly accessibleDashboardsById = computed(() => {
        const items = this.accessibleDashboards();
        if (!items || !items.length) return;

        const dashboards = new Map<string, IDashboardModel>();
        for (const item of items) {
            dashboards.set(item.Id, item);
        }
        return dashboards;
    });

    public readonly currentDashboard = computed(() => {
        const dashboards = this.accessibleDashboardsById();
        const currentId = this.currentId();
        if (!dashboards || !dashboards.size || !currentId) return;
        return dashboards.get(currentId);
    });

    public readonly currentApplicationType = computed(() => this.currentDashboard()?.ApplicationType);
    public readonly currentDashboardYear = computed(() => this.currentDashboard()?.TaxYear);
    public readonly currentDashboardType = computed(() => this.currentDashboard()?.ApplicationType);
    public readonly currentDashboardIsFirmTax = computed<boolean>(() => isiFirmTax(this.currentDashboard()?.ApplicationType));
    public readonly currentDashboardIsTaxPrep = computed<boolean>(() => isTaxprep(this.currentDashboard()?.ApplicationType));
    public readonly currentDashboardIsJobsBilling = computed<boolean>(() => isJobsBilling(this.currentDashboard()?.ApplicationType));
    public readonly currentDashboardUseiFIrmUserId = computed(() => {
        const isFirmTax = this.currentDashboardIsFirmTax();
        const isJobsBilling = this.currentDashboardIsJobsBilling();
        const year = this.currentDashboardYear();
        return isFirmTax || isJobsBilling || (year && year >= 2026);
    });
    public readonly currentDashboardClientCode = computed(() => this.currentDashboard()?.ClientCode);

    private readonly httpOptions = {
        headers: new HttpHeaders().set("Content-Type", "application/json"),
    };

    public reload(forceReload = false): void {
        if (this.authService.inReloginPrompted()) return;
        this.isReloading = forceReload;
        this.dashboardsResource.reload();
    }

    private async loadDashboards(): Promise<IDashboardModel[] | undefined> {
        try {
            const result = await firstValueFrom(this.http.get<IDashboardApiResponse>(this.getUrl(), this.httpOptions));
            if (result.Code == 200) return result.Dashboards;
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    public async getDashboard(id: string | undefined | null): Promise<IDashboardModel | undefined> {
        if (!id) return;
        await this.getDashboardsList();
        if (!this.dashboards()?.length) return;
        return this.dashboards()?.find((it) => it.Id === id);
    }

    public async getDashboardsList(): Promise<IDashboardModel[] | undefined> {
        await this.waitForDashboards();
        return this.dashboardsCached;
    }

    // Do not use it. Only use in getDashboardsList() for fallback compatibility. It will be removed in the future.
    private async waitForDashboards(): Promise<void> {
        await firstValueFrom(
            this.dashboards$.pipe(
                filter((dashboards) => dashboards !== undefined),
                take(1),
            ),
        );
    }

    public async getAccessibleDashboard(id: string | undefined | null): Promise<IDashboardModel | undefined> {
        if (!id) return;
        await this.getAccessibleDashboardsList();
        if (!this.accessibleDashboards()?.length) return;
        return this.accessibleDashboards()?.find((it) => it.Id === id);
    }

    public async getAccessibleDashboardsList(): Promise<IDashboardModel[] | undefined> {
        await this.waitForAccessibleDashboards();
        return this.accessibleDashboards();
    }

    // Do not use it. Only use in getAccessibleDashboardsList() for fallback compatibility. It will be removed in the future.
    private async waitForAccessibleDashboards(): Promise<void> {
        await firstValueFrom(
            this.accessibleDashboards$.pipe(
                filter((dashboards) => dashboards !== undefined),
                take(1),
            ),
        );
    }

    public updateDashboard(dashboardId: string | number, dashboardData: IDashboardUpdateRequest): Observable<IDashboardUpdateResponse> {
        return this.http.put<IDashboardUpdateResponse>(this.getUrl() + dashboardId, dashboardData, this.httpOptions);
    }

    private getUrl(path = "dashboard/"): string {
        return (
            (this.appSettingsService.getSetting("DashboardsService_URL") ?? "") +
            (this.appSettingsService.getSetting("DashboardsService_Version") ?? "") +
            path
        );
    }

    public saveAssignCategoryNoTags(dashboard: IDashboardModel, newValue: boolean, newCategoryId: string): Observable<IDashboardUpdateResponse> {
        return this.http.put<IDashboardUpdateResponse>(
            this.getUrl() + dashboard.Id,
            { AssignCategoryNoTags: newValue, AssignCategoryId: newCategoryId },
            this.httpOptions,
        );
    }

    public async getCurrentDashboard(allowDisabledUserDashboards = false): Promise<IDashboardModel | undefined> {
        return await this.getDashboard(await this.getSafeCurrentDashboardId(allowDisabledUserDashboards));
    }
    public async getSafeCurrentDashboardId(allowDisabledUserDashboards = false): Promise<string | undefined | null> {
        if (!allowDisabledUserDashboards) await this.getAccessibleDashboardsList();
        else await this.getDashboardsList();
        let currentDashboardId = await this.getCurrentDashboardId(allowDisabledUserDashboards);
        if (!currentDashboardId) {
            await this.getDashboardsList();
            currentDashboardId = await this.getCurrentDashboardId(allowDisabledUserDashboards);
        }
        return currentDashboardId;
    }
    private async getCurrentDashboardId(allowDisabledUserDashboards = false): Promise<string | undefined | null> {
        const key = await this.getDashboardIdKey();
        if (!key) return;
        let saveDashboardId = false;
        const initialDashboardId = this.currentDashboardId;
        let currentDashboardId = initialDashboardId;

        if (!currentDashboardId) {
            currentDashboardId = sessionStorage.getItem(key);
            if (!currentDashboardId) {
                saveDashboardId = true;
                currentDashboardId = localStorage.getItem(key);
            }
        }
        let dashboardIsInaccessible = !currentDashboardId;
        if (!dashboardIsInaccessible) {
            dashboardIsInaccessible = !(await this.getDashboard(currentDashboardId));
            if (!dashboardIsInaccessible && !allowDisabledUserDashboards) {
                dashboardIsInaccessible = !(await this.getAccessibleDashboard(currentDashboardId));
            }
        }
        if (dashboardIsInaccessible) {
            saveDashboardId = true;
            currentDashboardId = await this.getFirstAccessibleDashboardId();
        }
        // Another caller (e.g. dashboardIdGuard) set the dashboard while we were awaiting; don't overwrite it with a stale id.
        if (this.currentDashboardId !== initialDashboardId) return this.currentDashboardId;
        this.setCurrentDashboardId(currentDashboardId, saveDashboardId);
        return this.currentDashboardId;
    }

    public getCurrentDashboardIdOrThrow(): string {
        const dashboardId = this.currentId();

        if (!dashboardId) throw new Error("Cannot request cards before the current dashboard is initialized.");

        return dashboardId;
    }

    private async getFirstAccessibleDashboardId(): Promise<string | undefined> {
        await this.getAccessibleDashboardsList();
        if (this.accessibleDashboards()?.length) {
            return this.accessibleDashboards()?.sort((a, b) => (!b.TaxYear ? -1 : !a.TaxYear ? 1 : b.TaxYear - a.TaxYear))[0].Id;
        }
    }

    public async setCurrentDashboardId(id: string | undefined | null, save = true): Promise<void> {
        if (!id) return;
        const key = await this.getDashboardIdKey();
        if (!key) return;
        this.currentDashboardId = id;
        this._currentId.set(id);
        if (save) {
            sessionStorage.setItem(key, id);
            LocalStorageService.setItem(key, id);
            this.currentDashboarChangedEmitter.next(id);
        }
    }

    private async getDashboardIdKey(): Promise<string | undefined> {
        if (!this.dashboardIdKeyCached) await this.waitForDashboardIdKey();
        return this.dashboardIdKeyCached;
    }

    // Do not use it. Only use in getDashboardIdKey() for fallback compatibility. It will be removed in the future.
    private async waitForDashboardIdKey(): Promise<void> {
        await firstValueFrom(
            this.dashboardIdKey$.pipe(
                filter((dashboardIdKey) => dashboardIdKey !== undefined && dashboardIdKey !== null && dashboardIdKey !== ""),
                take(1),
            ),
        );
    }

    useiFirmUserId(dashboard: IDashboardModel | null | undefined): boolean {
        return (
            !dashboard ||
            isJobsBillingDashboard(dashboard) ||
            isiFirmTaxDashboard(dashboard) ||
            (dashboard.TaxYear !== null ? dashboard.TaxYear >= 2026 : false)
        );
    }
}
